import "../style.css";
import { ArrowDownward } from "@mui/icons-material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import {
  Box,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { commonTableStyles as styles, TABLE_TOKENS } from "../styles";
import { ReducersModal } from "utils/modals";
import { UPDATE_SERVICE_TYPE_REDUCER } from "store/actionTypes";
import ContainedButton from "components/button/contained-button";
import { getServiceTypesListing } from "modules/serviceManagement/action";
import Loader from "components/loader";
import CategoryTabs from "components/category/CategoryTabs";
import { SERVICE_CATEGORY, toServiceCategory } from "utils/serviceCategory";
import ROUTE_NAMES from "routes/routeNames";
import { displayOrNA } from "utils/commonFunctions";

const ServicesTable = () => {
  const { result, pagination, loader, category, page, limit } = useSelector(
    (state: ReducersModal) => state.ServiceTypeReducer
  );
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const [isSort, setIsSort] = useState<{ [key: string]: boolean }>({
    createdAt: false,
  });

  const dispatchValue = (payload: any) => {
    dispatch({
      type: UPDATE_SERVICE_TYPE_REDUCER,
      payload,
    });
  };

  useEffect(() => {
    dispatch(getServiceTypesListing());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, page, limit]);

  const handleCategoryChange = (nextCategory: SERVICE_CATEGORY) => {
    if (nextCategory === toServiceCategory(category)) return;
    dispatchValue({ category: nextCategory, page: 1, result: [] });
  };

  const handleSort = (sortCol: string) => {
    const newObject: any = {};
    Object.keys(isSort).forEach(function sort(key) {
      if (key === sortCol) {
        newObject[key] = !isSort[key];
      } else {
        newObject[key] = false;
      }
    });
    setIsSort(newObject);
  };

  const handleChangePage = (
    _: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    dispatchValue({ page: newPage + 1 });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    dispatchValue({ limit: event.target.value, page: 1 });
  };

  const getServiceId = (val: any) => val?._id ?? "";

  const openServicePage = (serviceId: string, edit = false) => {
    if (!serviceId) return;
    const path = `${ROUTE_NAMES.MAIN_ROUTES.SERVICE_DETAIL}?serviceId=${serviceId}${
      edit ? "&edit=true" : ""
    }`;
    navigate(path);
  };

  const renderLoading = () => {
    return (
      <React.Fragment>
        <Skeleton
          height={500}
          width={"100%"}
          variant="rectangular"
          sx={{ borderRadius: "8px", marginTop: "16px" }}
        />
      </React.Fragment>
    );
  };

  const renderTable = () => {
    if (loader && result.length === 0) {
      return renderLoading();
    }
    return (
      <Box sx={styles.paper}>
        <TableContainer>
          <Table sx={styles.table}>
            <TableHead sx={{ ...styles.tableHeader }}>
              <TableRow sx={styles.headerRow}>
                <TableCell sx={styles.headingTitleText}>{"S.No."}</TableCell>
                <TableCell sx={styles.headingTitleText}>{"Service Type"}</TableCell>
                <TableCell sx={styles.headingTitleText}>
                  {"Created On"}
                  <ArrowDownward
                    sx={{
                      ...styles.dwnIcnStyl,
                      ...(isSort["createdAt"] && { ...styles.rotateIcon }),
                    }}
                    onClick={() => handleSort("createdAt")}
                  />
                </TableCell>
                <TableCell sx={styles.headingTitleText}>{"Actions"}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {result && result.length > 0 ? (
                result.map((val: any, index) => {
                  const serialNumber =
                    ((pagination?.page || 1) - 1) * (pagination?.limit || 10) +
                    index +
                    1;
                  const serviceId = getServiceId(val);
                  const serviceName = displayOrNA(val?.serviceName);
                  return (
                    <TableRow
                      key={val._id ?? index}
                      sx={styles.row}
                      hover
                    >
                      <TableCell>{serialNumber}</TableCell>
                      <TableCell sx={{ textTransform: "capitalize" }}>
                        {serviceId ? (
                          <div
                            className="name-field"
                            onClick={() => openServicePage(serviceId)}
                          >
                            {serviceName}
                          </div>
                        ) : (
                          <>N/A</>
                        )}
                      </TableCell>
                      <TableCell>
                        {val?.createdAt
                          ? moment(val.createdAt).format("DD/MM/YYYY")
                          : "N/A"}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "middle" }}>
                        <Box sx={styles.action_table_cell_container}>
                          <BorderColorIcon
                            style={{ color: TABLE_TOKENS.accent, fontSize: "1.25rem" }}
                            onClick={() => openServicePage(serviceId, true)}
                          />
                          <VisibilityIcon
                            style={{ color: TABLE_TOKENS.accent, fontSize: "1.25rem" }}
                            onClick={() => openServicePage(serviceId)}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={15} sx={styles.noDataCell}>
                    <Box>{"No Data Found"}</Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={styles.paginationContainer}>
          <TablePagination
            component="div"
            count={pagination.totalRecords}
            page={Math.max((pagination.page || 1) - 1, 0)}
            onPageChange={handleChangePage}
            rowsPerPage={pagination.limit}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      <Box sx={{ ...styles.searchAndFilter, marginBottom: "16px" }}>
        <CategoryTabs
          basePath={ROUTE_NAMES.MAIN_ROUTES.SERVICE_MANAGEMENT}
          onChange={handleCategoryChange}
        />
        <ContainedButton
          externalStyles={{
            ...styles.HeadBtns,
            width: "auto",
            minWidth: "168px",
            whiteSpace: "nowrap",
          }}
          onClick={() => {
            navigate(ROUTE_NAMES.MAIN_ROUTES.ADD_SERVICE);
          }}
          title={"Add Service Type"}
        />
      </Box>
      {renderTable()}
      {loader && <Loader />}
    </Box>
  );
};

export default ServicesTable;
